import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { loginRequest } from '../../../redux/actions/authActions';
import { sendAccountActivationRequest } from '../../../redux/actions/emailActions';
import logo from '../../../images/logo1_aveiro.png';
import './login.css';
import ForgotPasswordModal from './ForgotPasswordModal';

/* ══════════════════════════════════════════════════════════════
   ACTIVATION MODAL
══════════════════════════════════════════════════════════════ */
const ActivationModal = ({ onClose, t, dispatch, activationLoading, activationMessage, activationError }) => {
    const [step, setStep]           = useState('form'); // 'form' | 'done'
    const [username, setUsername]   = useState('');
    const [localError, setLocalError] = useState(null);

    useEffect(() => {
        if (activationLoading) setStep('sending');
    }, [activationLoading]);

    useEffect(() => {
        if (activationMessage) {
            setLocalError(null);
            setStep('done');
        }
    }, [activationMessage]);

    useEffect(() => {
        if (activationError) {
            setLocalError(
                activationError.message ||
                activationError ||
                t('Une erreur est survenue. Veuillez réessayer.')
            );
            setStep('form');
        }
    }, [activationError, t]);

    const handleSend = (e) => {
        e.preventDefault();
        if (!username.trim()) {
            setLocalError(t('Veuillez saisir votre username.'));
            return;
        }
        setLocalError(null);
        dispatch(sendAccountActivationRequest(username.trim()));
    };

    return (
        <div className="cm-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="cm-card">

                {/* ── Header ── */}
                <div className="cm-head">
                    <div className="cm-head-icon">
                        {step === 'done' ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="rgba(255,255,255,0.9)"/>
                            </svg>
                        ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                <path d="M12 1C8.676 1 6 3.676 6 7v1H4a1 1 0 00-1 1v13a1 1 0 001 1h16a1 1 0 001-1V9a1 1 0 00-1-1h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 110 4 2 2 0 010-4z" fill="rgba(255,255,255,0.9)"/>
                            </svg>
                        )}
                    </div>
                    <h2 className="cm-head-title">
                        {step === 'done' ? t('Activation envoyée') : t('Activer mon compte')}
                    </h2>
                    <p className={`cm-head-sub${step === 'done' ? ' sent' : ''}`}>
                        {step === 'done'
                            ? t('Votre demande a bien été traitée')
                            : t('Saisissez votre identifiant pour recevoir votre mot de passe initial')}
                    </p>
                    <button className="cm-close" onClick={onClose} aria-label="Fermer">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                            <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                        </svg>
                    </button>
                </div>

                {/* ── Body ── */}
                <div className="cm-body">

                    {/* Étape formulaire */}
                    {step !== 'done' && (
                        <form onSubmit={handleSend}>
                            {localError && (
                                <div className="cm-err">
                                    {/*<svg width="13" height="13" viewBox="0 0 24 24" fill="#be123c" style={{flexShrink:0,marginTop:1}}>*/}
                                    {/*    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>*/}
                                    {/*</svg>*/}
                                    {localError}
                                </div>
                            )}

                            <label className="cm-label">{t('Identifiant')}</label>
                            <div className="cm-field">
                                <input
                                    className="cm-input"
                                    type="text"
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    disabled={activationLoading}
                                    autoFocus
                                />
                            </div>

                            <button type="submit" className="cm-btn" disabled={activationLoading}>
                                {activationLoading ? (
                                    <><span className="cm-spin"/>{t('Envoi en cours...')}</>
                                ) : (
                                    <>
                                        <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
                                            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                                        </svg>
                                        {t("Envoyer l'activation")}
                                    </>
                                )}
                            </button>
                            {/*<button type="button" className="cm-btn-outline" onClick={onClose} disabled={activationLoading}>*/}
                            {/*    {t('Annuler')}*/}
                            {/*</button>*/}
                        </form>
                    )}

                    {/* Étape succès */}
                    {step === 'done' && (
                        <div className="cm-success">
                            <div className="cm-success-ring">
                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                                    <circle cx="14" cy="14" r="12" stroke="rgba(42,92,169,.2)" strokeWidth="1.5"/>
                                    <polyline
                                        className="cm-check-path"
                                        points="8,14 12,18 20,10"
                                        fill="none"
                                        stroke="#2a5ca9"
                                        strokeWidth="2.2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </div>

                            <div className="cm-success-badge">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="#2a5ca9">
                                    <path d="M20 4H4C2.9 4 2 4.9 2 6v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                                </svg>
                                {t('Email envoyé')}
                            </div>

                            <h3 className="cm-success-title">{t('Vérifiez votre boîte mail !')}</h3>
                            <p className="cm-success-desc">
                                {t('Un email d\'activation a été envoyé à :')}{' '}
                                <strong>{username}</strong>.<br/>
                                {t('Pensez à vérifier vos spams si besoin.')}
                            </p>

                            <button className="cm-btn" onClick={onClose}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="white">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                                </svg>
                                {t('Compris, retour à la connexion')}
                            </button>

                            <button
                                className="cm-btn-outline"
                                onClick={() => { setStep('form'); setUsername(''); setLocalError(null); }}
                            >
                                {t('Réessayer avec un autre username')}
                            </button>

                            <div className="cm-hr"/>
                            <p className="cm-hint">{t('Les accès temporaires sont envoyés uniquement si le compte n\'est pas encore activé.')}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
   LOGIN PAGE
══════════════════════════════════════════════════════════════ */
const Login = () => {
    const { t, i18n }                     = useTranslation();
    const [email, setEmail]               = useState('');
    const [password, setPassword]         = useState('');
    const [rememberMe, setRememberMe]     = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
    const [showActivationModal, setShowActivationModal]         = useState(false);

    const { activationLoading, activationMessage, error: activationError } = useSelector(
        state => state.emails || { activationLoading: false, activationMessage: '', error: null }
    );

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { loading, error, isLogged, isFirstLogin, accountStatus } = useSelector(
        state => state.auth || { loading: false, error: null, isLogged: false, isFirstLogin: false, accountStatus: null }
    );

    const isInactive = accountStatus === 'inactive';

    useEffect(() => {
        if (isLogged && !isFirstLogin) navigate('/cards', { replace: true });
        if (isFirstLogin) setShowActivationModal(true);
    }, [isLogged, isFirstLogin, navigate]);

    useEffect(() => {
        const isRTL = i18n.language === 'ar';
        document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
        document.documentElement.setAttribute('lang', i18n.language);
        document.body.classList.toggle('rtl-layout', isRTL);
    }, [i18n.language]);

    const handleSubmit = (e) => {
        e.preventDefault();
        dispatch(loginRequest({ usernameOrEmail: email, password, rememberMe }));
    };

    return (
        <>
            {/* ── Arrière-plan animé ── */}
            <div className="scene">
                <div className="grid-overlay"/>
                <div className="wave-container">
                    <div className="wave w1"/>
                    <div className="wave w2"/>
                    <div className="wave w3"/>
                </div>
            </div>

            {/* ── Layout principal ── */}
            <div className="login-layout">

                {/* ── Panneau gauche — Branding ── */}
                <div className="brand-panel">
                    <div className="brand-logo">
                        <div className="brand-logo-circle">
                            <img src={logo} alt="Logo"/>
                        </div>
                    </div>

                    <h1 className="brand-headline">
                        <span style={{ whiteSpace: 'nowrap' }}>{t('Gérez vos')} <span className="accent">{t('ressources')}</span></span>
                        <br/>{t('humaines')}
                    </h1>

                    <p className="brand-desc">
                        {t('Votre espace de travail digital tout-en-un, conçu pour simplifier votre quotidien et centraliser vos outils métier.')}
                    </p>

                    <div className="stats-row">
                        <div className="stat-item">
                            <div className="stat-number">360<span>°</span></div>
                            <div className="stat-label">{t('Gestion RH')}</div>
                        </div>
                        <div className="stat-divider"/>
                        <div className="stat-item">
                            <div className="stat-number">24<span>h</span></div>
                            <div className="stat-label">{t('Accessible')}</div>
                        </div>
                        <div className="stat-divider"/>
                        <div className="stat-item">
                            <div className="stat-number">100<span>%</span></div>
                            <div className="stat-label">{t('Sécurisé')}</div>
                        </div>
                    </div>
                </div>

                {/* ── Panneau droit — Carte login ── */}
                <div className="login-panel">
                    <div className="login-card">
                        <div className="card-stripe"/>

                        <div className="card-body">
                            <div className="card-header">
                                <h2 className="card-title">{t('Connexion sécurisée')}</h2>
                            </div>

                            {/* Erreur login */}
                            {error && (
                                <div className="login-error">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#be123c" style={{flexShrink:0,marginTop:1}}>
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                    </svg>
                                    {error.message || t('Identifiants incorrects.')}
                                </div>
                            )}

                            {/* Bannière compte inactif */}
                            {isInactive && (
                                <div className="inactive-banner">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="#d97706" style={{flexShrink:0}}>
                                        <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                                    </svg>
                                    {t("Votre compte n'est pas encore activé. Cliquez ci-dessous.")}
                                </div>
                            )}

                            {/* Formulaire */}
                            <form onSubmit={handleSubmit}>
                                <div className="field-group">
                                    <label className="field-label" htmlFor="login-email">
                                        {t('Identifiant (Email ou téléphone)')}
                                    </label>
                                    <div className="field-wrap">
                                        <span className="field-icon">
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z"/>
                                            </svg>
                                        </span>
                                        <input
                                            className="field-input"
                                            type="email"
                                            id="login-email"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                    </div>
                                </div>

                                <div className="field-group">
                                    <label className="field-label" htmlFor="login-password">
                                        {t('Mot de passe')}
                                    </label>
                                    <div className="field-wrap">
                                        <span className="field-icon">
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                                <path d="M18 8H17V6C17 3.24 14.76 1 12 1C9.24 1 7 3.24 7 6V8H6C4.9 8 4 8.9 4 10V20C4 21.1 4.9 22 6 22H18C19.1 22 20 21.1 20 20V10C20 8.9 19.1 8 18 8ZM12 17C10.9 17 10 16.1 10 15C10 13.9 10.9 13 12 13C13.1 13 14 13.9 14 15C14 16.1 13.1 17 12 17ZM15.1 8H8.9V6C8.9 4.29 10.29 2.9 12 2.9C13.71 2.9 15.1 4.29 15.1 6V8Z"/>
                                            </svg>
                                        </span>
                                        <input
                                            className="field-input"
                                            type={showPassword ? 'text' : 'password'}
                                            id="login-password"
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            placeholder="••••••••••"
                                            required
                                            disabled={loading}
                                            style={{paddingRight: '44px'}}
                                        />
                                        <button
                                            type="button"
                                            className="toggle-password"
                                            onClick={() => setShowPassword(!showPassword)}
                                            disabled={loading}
                                        >
                                            {showPassword ? (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 7C14.76 7 17 9.24 17 12C17 12.65 16.87 13.26 16.64 13.83L19.56 16.75C21.07 15.49 22.26 13.86 22.99 12C21.26 7.61 16.99 4.5 11.99 4.5C10.59 4.5 9.25 4.75 8.01 5.2L10.17 7.36C10.74 7.13 11.35 7 12 7ZM2 4.27L4.28 6.55L4.73 7C3.08 8.3 1.78 10.02 1 12C2.73 16.39 7 19.5 12 19.5C13.55 19.5 15.03 19.2 16.38 18.66L16.8 19.08L19.73 22L21 20.73L3.27 3L2 4.27Z"/>
                                                </svg>
                                            ) : (
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                    <path d="M12 4.5C7 4.5 2.73 7.61 1 12C2.73 16.39 7 19.5 12 19.5C17 19.5 21.27 16.39 23 12C21.27 7.61 17 4.5 12 4.5ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM12 9C10.34 9 9 10.34 9 12C9 13.66 10.34 15 12 15C13.66 15 15 13.66 15 12C15 10.34 13.66 9 12 9Z"/>
                                                </svg>
                                            )}
                                        </button>
                                    </div>
                                </div>

                                <div className="form-options">
                                    <div className="remember-me">
                                        <input
                                            type="checkbox"
                                            id="rememberMe"
                                            checked={rememberMe}
                                            onChange={e => setRememberMe(e.target.checked)}
                                            disabled={loading}
                                        />
                                        <label htmlFor="rememberMe">{t('Se souvenir de moi')}</label>
                                    </div>
                                    <a
                                        href="#"
                                        className="forgot-password"
                                        onClick={e => { e.preventDefault(); setShowForgotPasswordModal(true); }}
                                    >
                                        {t('Mot de passe oublié ?')}
                                    </a>
                                </div>

                                <button type="submit" className="login-button" disabled={loading}>
                                    {loading ? (
                                        <><span className="av-spinner"/>{t('Connexion en cours...')}</>
                                    ) : (
                                        <>
                                            <svg width="15" height="15" viewBox="0 0 24 24" fill="white">
                                                <path d="M11 7L9.6 8.4l2.6 2.6H2v2h10.2l-2.6 2.6L11 17l5-5-5-5zm9 12h-8v2h8c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2h-8v2h8v14z"/>
                                            </svg>
                                            {t('Se connecter')}
                                        </>
                                    )}
                                </button>
                            </form>

                            {/* ── Bouton Activer mon compte ── */}
                            <button
                                type="button"
                                className={`cicada-activate-btn${isInactive ? ' inactive' : ''}`}
                                onClick={() => setShowActivationModal(true)}
                                disabled={loading}
                            >
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 1C8.676 1 6 3.676 6 7v1H4a1 1 0 00-1 1v13a1 1 0 001 1h16a1 1 0 001-1V9a1 1 0 00-1-1h-2V7c0-3.324-2.676-6-6-6zm0 2c2.276 0 4 1.724 4 4v1H8V7c0-2.276 1.724-4 4-4zm0 9a2 2 0 110 4 2 2 0 010-4z"/>
                                </svg>
                                {t('Première connexion ? Activez votre compte')}
                            </button>

                            {/*<div className="terms-link">*/}
                            {/*    <a href="#">{t("Conditions générales d'utilisation")}</a>*/}
                            {/*</div>*/}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Sélecteur de langue ── */}
            <div className="language-selector">
                {['fr', 'en', 'ar'].map((lng, i, arr) => (
                    <React.Fragment key={lng}>
                        <button
                            onClick={() => i18n.changeLanguage(lng)}
                            className={i18n.language === lng ? 'active' : ''}
                        >
                            {lng.toUpperCase()}
                        </button>
                        {i < arr.length - 1 && <span className="language-separator">|</span>}
                    </React.Fragment>
                ))}
            </div>

            {/* ── Modal activation (Redux) ── */}
            {showActivationModal && (
                <ActivationModal
                    onClose={() => setShowActivationModal(false)}
                    t={t}
                    dispatch={dispatch}
                    activationLoading={activationLoading}
                    activationMessage={activationMessage}
                    activationError={activationError}
                />
            )}

            {/* ── Modal mot de passe oublié ── */}
            {showForgotPasswordModal && (
                <ForgotPasswordModal onClose={() => setShowForgotPasswordModal(false)}/>
            )}
        </>
    );
};

export default Login;